import { LngLat, Map } from 'maplibre-gl';

/**
 * Pans by sliding a frozen snapshot of the map instead of re-rendering every frame, but only on
 * sessions slow enough to need it.
 *
 * On a GPU-less Citrix/VDI session the drag is presentation-bound, not compute-bound: MapLibre needs
 * ~3ms per frame and rasterisation is free, but the main thread blocks 60-140ms per frame handing a
 * canvas whose every pixel changed to the compositor. Repainting the canvas is what makes each frame
 * a new texture; a CSS transform on an unchanged layer is not. So while a drag runs we skip
 * painter.render() entirely and translate a snapshot instead. The map still updates its transform
 * and keeps loading tiles, and one real render happens on moveend.
 *
 * That trade is only worth making when frames are expensive: the map stops updating live while
 * dragging, so newly exposed area stays blank until the snapshot is re-taken (see REFRESH_FRACTION)
 * or you release. On a machine with a GPU the drag is already at 60fps and the preview would be a
 * pure downgrade.
 *
 * Hence the gate is a *measurement*, not a capability sniff. Every drag starts rendering normally
 * while frame intervals are sampled; the preview only takes over once the median frame is slower
 * than SLOW_FRAME_MS. Deliberately not WEBGL_debug_renderer_info: the Citrix sessions this was
 * written for do have an active GPU process and do not report a software rasteriser, so a renderer
 * string would miss exactly the case that needs help - and would misfire on anything that reports an
 * unfamiliar name. Frame time is the thing we actually care about, so measure that.
 *
 * Two traps this works around:
 * - The snapshot is a *separate* overlay element rather than the map canvas itself. MapLibre derives
 *   pointer positions from `canvas.getBoundingClientRect()` (DOM.getPoint), so transforming the real
 *   canvas - or any ancestor of it - would zero out the drag delta and freeze the gesture.
 * - The snapshot is taken from the map's own `render` event, which Map._render() fires synchronously
 *   right after painter.render(). That is still the frame's task, so the drawing buffer is valid and
 *   the map does not need `preserveDrawingBuffer` - which would cost a buffer copy on every frame,
 *   for the whole session, to serve one drawImage per gesture.
 */

/**
 * A frame slower than this counts as struggling: 25fps, against the ~16.7ms of a GPU session and the
 * 60-140ms measured on the VDI sessions. The gap between the two is wide enough that the exact value
 * hardly matters.
 */
const SLOW_FRAME_MS = 40;

/** Frames watched before deciding. A sliding window, so a session that degrades is picked up later. */
const SAMPLE_COUNT = 8;

/**
 * How far the snapshot may slide from where it was taken, as a fraction of the viewport, before it
 * is re-taken from a fresh frame. This bounds how much blank the drag can expose: at 0.5 at least
 * half the screen is still real map. Refreshing costs exactly the expensive frame the preview exists
 * to avoid, so it has to stay coarse - a long drag pays a handful of them, not one per frame.
 */
const REFRESH_FRACTION = 0.25;

export function attachPanPreview(map: Map) {
  const painter = (map as unknown as Record<string, unknown>)['painter'] as
    | { render?: (...args: unknown[]) => unknown }
    | undefined;
  const originalRender = painter?.render;
  if (!painter || typeof originalRender !== 'function') {
    // No painter to suspend means no saving to be had; leave rendering untouched.
    return;
  }

  /** The snapshot currently standing in for the map. */
  let overlay: HTMLCanvasElement | undefined;
  /** A snapshot kept on screen until a real frame has replaced it. */
  let stale: HTMLCanvasElement | undefined;
  let startCenter: LngLat | undefined;
  let startZoom = 0;
  let startBearing = 0;
  let previewing = false;
  let dragging = false;
  /** Set once the session has been measured as slow; later drags then preview from the first frame. */
  let knownSlow = false;
  let intervals: number[] = [];
  let lastRender = 0;

  painter.render = function (this: unknown, ...args: unknown[]) {
    if (previewing) {
      return undefined;
    }
    return originalRender.apply(this, args);
  };

  map.on('render', () => {
    if (stale) {
      // A real frame has just been drawn, so the canvas underneath is current again. Swapping any
      // earlier would flash the pre-gesture view for a frame.
      stale.remove();
      stale = undefined;
      if (!previewing) {
        map.getCanvas().style.visibility = '';
      }
    }
    if (!dragging || previewing) {
      return;
    }
    if (!knownSlow && !measureIsSlow()) {
      return;
    }
    knownSlow = true;
    try {
      overlay = createOverlay(map);
    } catch {
      // A lost context or a canvas we cannot read: fall back to normal rendering for this gesture.
      overlay = undefined;
      return;
    }
    // Recorded here rather than on movestart: this frame is what the snapshot shows, and the camera
    // has moved on since the gesture began.
    startCenter = map.getCenter();
    startZoom = map.getZoom();
    startBearing = map.getBearing();
    previewing = true;
  });

  /** Times this frame against the previous one and reports whether the drag is struggling. */
  function measureIsSlow(): boolean {
    const now = performance.now();
    const previous = lastRender;
    lastRender = now;
    if (!previous) {
      // First frame of the drag: nothing to measure against yet.
      return false;
    }
    intervals.push(now - previous);
    if (intervals.length > SAMPLE_COUNT) {
      intervals.shift();
    }
    return intervals.length === SAMPLE_COUNT && median(intervals) > SLOW_FRAME_MS;
  }

  /**
   * Hands the screen back to the real renderer, keeping the snapshot up until a real frame has
   * replaced it. If the drag is still running the render handler takes a new snapshot from that
   * frame, which is what makes a refresh a refresh rather than an end.
   */
  function suspendPreview() {
    if (!previewing) {
      return;
    }
    previewing = false;
    startCenter = undefined;
    stale = overlay;
    overlay = undefined;
    // The canvas has held the frame the snapshot was taken from all along; ask for a current one to
    // swap back to.
    map.triggerRepaint();
  }

  const stop = () => {
    dragging = false;
    intervals = [];
    lastRender = 0;
    suspendPreview();
  };

  map.on('movestart', (event) => {
    // Only real gestures. Programmatic camera moves - NavigationService's follow loop above all -
    // have no DOM event and must keep rendering normally.
    if (previewing || !(event as { originalEvent?: unknown }).originalEvent) {
      return;
    }
    dragging = true;
    intervals = [];
    lastRender = 0;
  });

  map.on('move', () => {
    if (!previewing || !overlay || !startCenter) {
      return;
    }
    // A snapshot can only stand in for a translation. zoomstart/rotatestart normally catch this
    // first, but a gesture that changes scale or angle without them (or before them) has to be
    // caught here, or the preview would slide a stale image instead of showing the real one.
    if (map.getZoom() !== startZoom || map.getBearing() !== startBearing) {
      stop();
      return;
    }
    const canvas = map.getCanvas();
    // Where the centre we froze has moved to. Translating by that offset reproduces the pan exactly,
    // including on a rotated map, because a pan is a pure screen-space translation.
    const moved = map.project(startCenter);
    const dx = moved.x - canvas.clientWidth / 2;
    const dy = moved.y - canvas.clientHeight / 2;
    overlay.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    // Applied before the check so the snapshot that stays up while the fresh frame is drawn sits
    // where the gesture left it, instead of jumping back a frame's worth of pan.
    if (
      Math.abs(dx) > canvas.clientWidth * REFRESH_FRACTION ||
      Math.abs(dy) > canvas.clientHeight * REFRESH_FRACTION
    ) {
      suspendPreview();
    }
  });

  // Anything that rescales or rotates the image has to drop straight back to real rendering.
  map.on('zoomstart', stop);
  map.on('rotatestart', stop);
  map.on('pitchstart', stop);
  map.on('moveend', stop);
  // A resize reallocates the canvas, which leaves the snapshot at the wrong size.
  map.on('resize', stop);
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

function createOverlay(map: Map): HTMLCanvasElement {
  const canvas = map.getCanvas();
  if (!canvas.parentElement) {
    throw new Error('map canvas is not attached');
  }

  const overlay = document.createElement('canvas');
  overlay.width = canvas.width;
  overlay.height = canvas.height;
  overlay.style.cssText = [
    'position:absolute',
    'top:0',
    'left:0',
    `width:${canvas.clientWidth}px`,
    `height:${canvas.clientHeight}px`,
    'pointer-events:none',
  ].join(';');

  const context = overlay.getContext('2d');
  if (!context) {
    throw new Error('no 2d context for the pan snapshot');
  }
  context.drawImage(canvas, 0, 0);
  // Directly after the canvas, not appended: Marker adds its element to this same container, so
  // appending would stack the snapshot on top of every marker and make them vanish while dragging.
  canvas.insertAdjacentElement('afterend', overlay);

  // visibility rather than display: the canvas keeps its box, which MapLibre measures for every
  // pointer position. display:none would zero that rect and break the drag.
  canvas.style.visibility = 'hidden';
  return overlay;
}
